import { Link } from "@inertiajs/react"

import AdminLayout from "../../../shared/AdminLayout"

interface AdminUserRow {
  id: number
  name: string
  isAdmin: boolean
  email: string
  statusText: string
  neverSignedIn: boolean
  editHref: string
}

interface AdminUsersProps {
  users: AdminUserRow[]
  adminChip: string
  rowSeparator: string
  addLabel: string
  newHref: string
}

const ROW_CLASSES = "flex items-center gap-3 border-b border-gray-100 py-3"
const LAST_ROW_CLASSES = "flex items-center gap-3 py-3"

// The admin users list (`Identity::AdminUsersController#index`,
// `/admin/users`). Implements docs/design/users.html's Block 2 verbatim:
// the `AdminContacts.tsx` row shape (tap-to-edit `<Link>`, chevron, last
// row drops its border, dashed «+ Добавить …» footer), an «админ» chip
// after the name for an admin's row only (FR-2.3), and a second line
// composing the muted e-mail, a translator-controlled separator and the
// sign-in status phrase. FR-4.5/FR-4.6 (amended in this same task): the
// amber warning colour marks the status span alone, never the e-mail
// beside it, and never the whole line. FR-2.5: the acting admin is always
// a row, so the list is never empty and needs no empty state.
export default function AdminUsers({ users, adminChip, rowSeparator, addLabel, newHref }: AdminUsersProps) {
  return (
    <AdminLayout>
      <ol className="flex flex-col">
        {users.map((user, index) => (
          <li key={user.id}>
            <Link
              href={user.editHref}
              className={index === users.length - 1 ? LAST_ROW_CLASSES : ROW_CLASSES}
            >
              <div className="flex grow flex-col gap-px">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold leading-[22px] text-gray-900">
                    {user.name}
                  </span>
                  {/* FR-2.3: the chip is a list element only -- role stays
                      editable solely through the form's own control. */}
                  {user.isAdmin && (
                    <span className="inline-flex items-center rounded-full bg-accent/5 px-2 py-px text-xs font-semibold text-accent">
                      {adminChip}
                    </span>
                  )}
                </div>
                <div className="text-[13px] leading-[18px] text-gray-500">
                  {user.email}
                  {rowSeparator}
                  {/* FR-4.5, FR-4.6: amber on the status phrase alone. */}
                  <span className={user.neverSignedIn ? "text-amber-700" : undefined}>
                    {user.statusText}
                  </span>
                </div>
              </div>
              <svg
                aria-hidden="true"
                className="text-gray-400"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M6 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </li>
        ))}
      </ol>
      <Link
        href={newHref}
        className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-gray-300 text-sm font-medium text-accent"
      >
        {addLabel}
      </Link>
    </AdminLayout>
  )
}
